import classNames from 'classnames';
import { memo, useCallback } from 'react';
import { FaCheck } from 'react-icons/fa';
import {
  V2QsStage,
  V2QsStageMileMarkerProps,
  defaultV2QsStageMileMarkerProps,
} from './types';

// V1과 동일한 히어로 이미지 (기본값으로 사용)
import defaultHeroImage from '../../../../../patient/question/typed-qs/assets/mile-marker-hero-computer.svg';

/**
 * V2QsStageMileMarker 컴포넌트
 *
 * 설문 진행 단계 마커 컴포넌트
 * V1 QsStageMileMarker의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 설문 진행 단계 시각화
 * - 완료/현재/대기 상태 표시
 * - 현재 단계 클릭 시 다음 진행
 */
function V2QsStageMileMarker(props: V2QsStageMileMarkerProps) {
  const {
    title,
    stages,
    onCurrentStageClick,
    heroImageUrl = defaultHeroImage, // V1과 동일하게 기본 이미지 사용
    completedLabel = defaultV2QsStageMileMarkerProps.completedLabel,
    className,
  } = props;

  const currentStageTitle =
    title || stages.find((stage) => stage.status === 'current')?.name || '';

  const handleStageClick = useCallback(
    (stage: V2QsStage) => {
      if (stage.status === 'current' && onCurrentStageClick) {
        onCurrentStageClick();
      }
    },
    [onCurrentStageClick]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, stage: V2QsStage) => {
      if ((e.key === 'Enter' || e.key === ' ') && stage.status === 'current') {
        e.preventDefault();
        onCurrentStageClick?.();
      }
    },
    [onCurrentStageClick]
  );

  return (
    <div className={classNames('flex-col gap-6 items-center', className)}>
      {/* 히어로 이미지 */}
      {heroImageUrl && (
        <img src={heroImageUrl} alt="" className="w-[60%] pt-12 pb-12" />
      )}

      {currentStageTitle && (
        <h2 className="self-start pl-4 pb-1 text-3xl font-bold">
          {currentStageTitle}
        </h2>
      )}

      {/* 단계 목록 */}
      <div className="flex-col p-4">
        {stages.map((stage, idx) => {
          const isCompleted = stage.status === 'completed';
          const isCurrent = stage.status === 'current';
          const isPending = stage.status === 'pending';

          return (
            <div key={stage.key}>
              {/* 단계 헤더 */}
              <div
                className={classNames(
                  'flex-row w-full items-center',
                  isCurrent && 'bg-white border-primary cursor-pointer'
                )}
                onClick={() => handleStageClick(stage)}
                onKeyDown={(e) => handleKeyDown(e, stage)}
                role={isCurrent ? 'button' : 'heading'}
                tabIndex={isCurrent ? 0 : -1}
              >
                {/* 상태 원형 아이콘 */}
                <div
                  className={classNames(
                    'w-8 h-8 min-w-8 rounded-full',
                    'border-2 z-10',
                    'flex-center',
                    isCompleted && 'bg-primary border-primary',
                    isPending && 'bg-white border-gray-300',
                    isCurrent && 'bg-white border-primary'
                  )}
                >
                  {isCompleted && (
                    <FaCheck
                      aria-label={completedLabel}
                      className="text-white"
                    />
                  )}
                </div>
                <h4
                  className={classNames(
                    'pl-4 text-lg font-semibold',
                    !isCurrent && 'opacity-50'
                  )}
                >
                  {stage.name}
                </h4>
              </div>

              {/* 연결선 및 설명 */}
              <div className="flex-row w-full min-h-6 items-stretch">
                <div className="w-8 min-w-8 relative">
                  {idx < stages.length - 1 && (
                    <div
                      className="border-l border-l-2 border-l-gray-300 absolute"
                      style={{ top: '-20px', bottom: '-20px', left: '50%' }}
                    />
                  )}
                </div>
                <div className="pl-4 pb-6 text-sm">
                  {isCurrent && stage.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(V2QsStageMileMarker);
